import toSnakeCase from 'decamelize-keys';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useCallback, useState } from 'react';
import { DonorWithWallet } from '../providers/DonorProvider/interfaces';
import { isAppleDevice } from '../components/Donee/components/common/utils';
import { copyToClipboard, copyToClipboardAsync } from '../utils/clipboard';
import { FeatureFlag } from '../constants';
import { useFeatureFlag } from '../providers';
import { usePushEvents } from './PushEvents';
import { getLoginTypeLocalStorage } from '../providers/DonorProvider/donorLocalStorage';
import { appDownloadEvent } from './PushEvents/clevertapEvents';
import { ApiHandler, Donee } from '../api';

const MOBILE_BREAKPOINT = 800;

type DeepLinkState = {
    loading: boolean;
    error: string;
    voucher: string;
};

type DeepLinkExtraData = {
    key: string;
    data: object;
};

export const getDoneeAmountDeepLink = (donee: Donee | undefined) =>
    `/dl/donee/${donee?.organization.doneeId}/amount`;

export const useDeepLink = (
    clickEventName: string = appDownloadEvent,
    eventPayload: object | undefined,
    callback: () => unknown,
) => {
    const { pushEvent } = usePushEvents();
    const { isFeatureEnabled } = useFeatureFlag();
    const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT - 1}px)`, {
        noSsr: true,
    });
    const [showCopiedModal, setShowCopiedModal] = useState<boolean>(false);
    const [deepLinkState, setDeepLinkState] = useState<DeepLinkState>({
        loading: false,
        error: '',
        voucher: '',
    });

    const isDeferredDeepLinkOn = useCallback(() => {
        return (
            isMobile &&
            (isAppleDevice()
                ? isFeatureEnabled(FeatureFlag.GIVING_OGA_DDL_AUTOLOGIN_IOS)
                : isFeatureEnabled(FeatureFlag.GIVING_OGA_DDL_AUTOLOGIN_AND))
        );
    }, [isFeatureEnabled, isMobile]);

    const getDeepLink = useCallback(
        async (
            donee: Donee | undefined,
            donor: DonorWithWallet | undefined | null,
            link: string,
            extraData: DeepLinkExtraData | undefined,
        ): Promise<string | undefined> => {
            const copyLink = `${window.location.origin}${link}`;

            if (!donor || !isDeferredDeepLinkOn()) {
                copyToClipboard(copyLink);
                if (isAppleDevice()) {
                    setShowCopiedModal(true);
                } else {
                    callback();
                }
                return copyLink;
            }

            const data = {
                doneeHashedId: donee?.organization.doneeId,
                donee,
                donor,
                lastLoginInfo: getLoginTypeLocalStorage(),
            };
            const result = extraData
                ? { ...data, [extraData.key]: extraData.data }
                : data;

            setShowCopiedModal(false);
            setDeepLinkState((prev) => ({
                ...prev,
                loading: true,
                error: '',
            }));

            // Kick off the API request. We must keep a reference to the
            // pending URL promise so we can schedule the clipboard write
            // synchronously below, while the user gesture is still active
            // (required by Safari/iOS).
            const apiPromise = ApiHandler.instance.auth.getAppMigrate(
                JSON.stringify(toSnakeCase(result, { deep: true })),
            );

            let resolveUrl: (url: string) => void;
            let rejectUrl: (reason?: unknown) => void;
            const urlPromise = new Promise<string>((resolve, reject) => {
                resolveUrl = resolve;
                rejectUrl = reject;
            });

            // Schedule the clipboard write NOW, inside the click gesture.
            // Safari/iOS accepts a Promise<Blob> here and resolves it later.
            const scheduledAsync = copyToClipboardAsync(urlPromise);

            try {
                const response = await apiPromise;
                const voucher = response.success
                    ? response.data.response?.payload?.voucher
                    : undefined;

                if (!response.success || !voucher) {
                    rejectUrl!(new Error('Failed to get voucher'));
                    setDeepLinkState((prev) => ({
                        ...prev,
                        loading: false,
                        error: 'Failed to get voucher',
                    }));
                    callback();
                    return;
                }

                if (eventPayload) {
                    pushEvent(clickEventName, {
                        ...eventPayload,
                        voucher,
                        link: copyLink,
                    });
                }

                const url = `${copyLink}${copyLink.includes('?') ? '&' : '?'}v=${voucher}`;
                resolveUrl!(url);
                if (!scheduledAsync) {
                    // Fallback path: no async ClipboardItem support, do it now.
                    copyToClipboard(url);
                }

                if (isAppleDevice()) {
                    setShowCopiedModal(true);
                } else {
                    callback();
                }

                setDeepLinkState({
                    loading: false,
                    error: '',
                    voucher,
                });

                return url;
            } catch {
                rejectUrl!(new Error('Failed to get voucher'));
                setDeepLinkState((prev) => ({
                    ...prev,
                    loading: false,
                    error: 'Failed to get voucher',
                }));
                callback();
            }
        },
        [
            isDeferredDeepLinkOn,
            eventPayload,
            pushEvent,
            clickEventName,
            callback,
        ],
    );

    return {
        deepLinkState,
        showCopiedModal,
        getDeepLink,
        isDeferredDeepLinkOn,
        setShowCopiedModal,
    };
};
